import { Fragment, useCallback, useId, useLayoutEffect, useRef, useState } from 'react';
import ApplicationIcon from '@/Components/ApplicationIcon';
import {
    Breadcrumb, BreadcrumbItem, BreadcrumbList, BreadcrumbPage, BreadcrumbSeparator,
} from '@/Components/ui/breadcrumb';
import { isSnowflake } from '@/lib/snowflake';
import { cn } from '@/lib/utils';
import { STAGE_ATTRIBUTE } from './stage';

/**
 * A vignette's breadcrumb trail, as the app's top bar sets it: the last
 * crumb is the current page; a crumb that is a snowflake id is set in mono,
 * as ids are everywhere else.
 */
export function Crumbs({ breadcrumb, className }) {
    return (
        <Breadcrumb className={cn('min-w-0', className)}>
            <BreadcrumbList className="flex-nowrap gap-1.5 text-[12.5px] sm:gap-1.5">
                {breadcrumb.map((crumb, index) => {
                    const mono = isSnowflake(crumb) ? 'font-mono text-[12px]' : null;
                    const last = index === breadcrumb.length - 1;

                    return (
                        <Fragment key={`${crumb}-${index}`}>
                            <BreadcrumbItem className="min-w-0">
                                {last ? (
                                    <BreadcrumbPage className={cn('truncate', mono)}>{crumb}</BreadcrumbPage>
                                ) : (
                                    <span className={cn('truncate', mono)}>{crumb}</span>
                                )}
                            </BreadcrumbItem>
                            {!last && <BreadcrumbSeparator />}
                        </Fragment>
                    );
                })}
            </BreadcrumbList>
        </Breadcrumb>
    );
}

/** ChromeBar's height (its h-10), in layout px: a window's content starts below it. */
export const CHROME_BAR_HEIGHT = 40;

/**
 * The thin top bar of the app, as a vignette shows it: the Synex X mark and a
 * static breadcrumb (Crumbs). `children` replaces the trail (a story window
 * that changes page draws its trails itself).
 */
export function ChromeBar({ breadcrumb = [], children }) {
    // One gradient per mark: several vignettes share a page.
    const gradientId = `vignette-mark-${useId().replace(/[^a-zA-Z0-9_-]/g, '')}`;

    return (
        <div className="flex h-10 shrink-0 items-center gap-2.5 border-b border-zinc-200/80 bg-white px-3.5">
            <ApplicationIcon className="size-4 shrink-0" gradientId={gradientId} />
            {(children || breadcrumb.length > 0) && (
                <>
                    <span className="h-4 w-px shrink-0 bg-zinc-200" aria-hidden="true" />
                    {children ?? <Crumbs breadcrumb={breadcrumb} />}
                </>
            )}
        </div>
    );
}

/**
 * The factor a scaled frame draws its content at, and the room it keeps for
 * it. The content is laid out at `100% / factor` of the frame's width and
 * scaled back down with a transform, so it keeps the app's desktop density;
 * the wrapper is given the scaled height, measured before paint and on every
 * resize, so the page around it never jumps.
 */
function useScaledBox({ scale, baseWidth }) {
    const outer = useRef(null);
    const inner = useRef(null);
    const [box, setBox] = useState({ width: 0, height: 0 });
    // A scale outside (0, 1) is not a size this frame draws at: 1:1.
    const fixed = typeof scale === 'number' && scale > 0 && scale < 1 ? scale : 1;
    const scaled = Boolean(baseWidth) || fixed !== 1;

    const measure = useCallback(() => {
        const width = outer.current?.clientWidth ?? 0;
        const height = inner.current?.offsetHeight ?? 0;
        setBox((current) => (current.width === width && current.height === height ? current : { width, height }));
    }, []);

    useLayoutEffect(() => {
        if (!scaled || !outer.current || !inner.current) return undefined;
        measure();

        if (typeof ResizeObserver === 'undefined') return undefined;
        const observer = new ResizeObserver(measure);
        observer.observe(outer.current);
        observer.observe(inner.current);

        return () => observer.disconnect();
    }, [scaled, measure]);

    // `baseWidth` fits the content's design width to the frame, never above
    // 1:1 (a wider frame lays the content out wider instead). Unmeasured (no
    // layout, as in a test) it draws at 1.
    const factor = !scaled
        ? 1
        : baseWidth
            ? (box.width > 0 ? Math.min(1, box.width / baseWidth) : 1)
            : fixed;

    // A new factor lays the content out at a new width, and its height
    // changes with it: measure again before the browser paints, or the
    // first frame would reserve the height of the layout it replaced (the
    // ResizeObserver would only catch up a frame later).
    useLayoutEffect(() => {
        if (scaled) measure();
    }, [scaled, factor, measure]);

    return { outer, inner, scaled, factor, height: box.height > 0 ? box.height * factor : undefined };
}

/**
 * The light app plate a vignette sits on: a white surface with a hairline
 * (rounded-mk-card), optionally the app's top bar, and the vignette inside.
 *
 * - `chrome` draws the top bar; `breadcrumb` is its trail, the last crumb the
 *   current page (e.g. ['Passports', 'EVP-750 Pack', 'pass_…']).
 * - `scale` (0 to 1) draws the content, top bar included, at that size, so a
 *   desktop-density fragment fits a small bento cell. `baseWidth` (px) does
 *   the same by fitting a design width to whatever width the frame gets.
 *   Either way the frame reserves exactly the scaled height.
 * - `bare` is for a frame inside a BezelFrame: it fills the bezel's plate,
 *   white even on a dark section, with no hairline or radius of its own (the
 *   bezel already draws both).
 *
 * A scaled frame is a stage: the box its content is laid out in carries
 * STAGE_ATTRIBUTE, and the stylesheet answers every viewport-size media query
 * inside it for a desktop window (STAGE_VIEWPORT, see stage.js and
 * resources/css/vignetteStage.js). So the app's components draw their desktop
 * variants (`sm:`, `lg:`, `max-md:` ...) on a phone too, as they do at 1440,
 * only smaller; and a sticky element in it stays where it is laid out (WebKit
 * would pin a `sticky bottom-0` one to the stage's drawn, scaled height).
 * JavaScript that reads the window (`matchMedia`, `innerWidth`) still reads
 * the visitor's screen: a vignette chooses its layout by props, never by it.
 */
export default function VignetteFrame({
    children,
    chrome = false,
    breadcrumb = [],
    scale = 1,
    baseWidth = null,
    bare = false,
    className,
    contentClassName,
}) {
    const { outer, inner, scaled, factor, height } = useScaledBox({ scale, baseWidth });

    const body = (
        <>
            {chrome && <ChromeBar breadcrumb={breadcrumb} />}
            <div className={cn('min-w-0', contentClassName)}>{children}</div>
        </>
    );

    return (
        <div
            ref={outer}
            data-vignette-frame=""
            className={cn(
                'relative overflow-hidden bg-white text-left text-zinc-950 antialiased',
                bare ? 'h-full' : 'rounded-mk-card ring-1 ring-mk-line',
                className,
            )}
        >
            {scaled ? (
                // transition-none: under reduced motion app.css gives every
                // element a 0.01ms transition on `all`, which would paint
                // one frame of the unscaled layout before the measured one.
                <div style={{ height }} className="overflow-hidden transition-none">
                    <div
                        ref={inner}
                        data-vignette-scale={factor}
                        {...{ [STAGE_ATTRIBUTE]: '' }}
                        className="transition-none"
                        style={{
                            width: `${100 / factor}%`,
                            transform: `scale(${factor})`,
                            transformOrigin: 'top left',
                        }}
                    >
                        {body}
                    </div>
                </div>
            ) : body}
        </div>
    );
}
